'use client';

import { useUser } from '@hooks/use-user';
import React, { useEffect, useState } from 'react';
import { ICONS } from '@react-ui-kit/icons/icon-paths';
import { Box, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import { PROFILE_HEADER_LINKS } from '@helpers/constants';
import { CustomIconButton } from '@react-ui-kit/buttons/custom-icon-button';
import Image from 'next/image';
import { useRouter } from 'next/navigation';

export function ClientHeader({ serverUserId }: { serverUserId: string }) {
  const { userData, isUserDataLoading, userError, mutateLogoutUser } =
    useUser();
  const router = useRouter();

  const [displayData, setDisplayData] = useState({
    userName: serverUserId,
    avatarUrl: ICONS.QUESTION
  });

  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorElement);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElement(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorElement(null);
  };

  useEffect(() => {
    if (userData && !isUserDataLoading && !userError) {
      console.log('✅ Updating client user data', userData);
      setDisplayData({
        userName: userData.userName,
        avatarUrl: userData.avatarUrl ?? ICONS.QUESTION
      });
    }
  }, [userData, isUserDataLoading, userError]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center'
        }}
      >
        <CustomIconButton
          width={40}
          height={40}
          icon={displayData.avatarUrl}
          tooltipText="User Menu"
          onClick={handleClick}
        />
        <Typography variant="caption" color="success" component="p">
          {displayData.userName}
        </Typography>
      </Box>
      <Menu
        anchorEl={anchorElement}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1
              },
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0
              }
            }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {PROFILE_HEADER_LINKS.map((link, index) => (
          <MenuItem
            key={index}
            onClick={async () => {
              if (link.name === 'Logout') {
                await mutateLogoutUser();
                router.push('/');
              } else if (
                link.name === 'Login' &&
                userData?.userName !== 'anonymous' &&
                !isUserDataLoading &&
                !userError
              ) {
                router.push(`/profile`);
              } else {
                router.push(link.link);
              }
            }}
            sx={{
              display:
                link.protected && (!userData || !userData?.isLoggedIn)
                  ? 'none'
                  : 'flex',
              gap: 2,
              padding: '1rem 2rem 1rem 1rem',
              '&:hover': {
                bgcolor: 'primary.main',
                color: 'background.paper'
              }
            }}
          >
            {link.icon && (
              <ListItemIcon>
                <Image
                  alt="Icon Menu Item"
                  src={link.icon}
                  width={25}
                  height={25}
                />
              </ListItemIcon>
            )}
            {link.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
